import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraSpinner } from '@mono/ui-components/ensigns';
import { Testids } from '@mono/ui-selectors';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { useRecordDocuments } from '../documents/hooks';
import { documentsQueryState } from '../documents/recordDocumentsSection';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { CompanyUpload, useCompanyIntel } from './company/CompanyActions';
import { CompanyFamilySection } from './company/CompanyFamily';
import { CompanyHeader } from './company/CompanyHeader';
import { CompanyRateBookTable } from './company/CompanyRateBookTable';
import { CorporateHistory } from './company/CorporateHistory';
import { companyRail } from './company/companyRail';
import { useCompany } from './company/hooks';
import { PartnerControls } from './company/PartnerControls';
import { PeopleWeKnow } from './company/PeopleWeKnow';
import { SbaProgramsSection } from './company/SbaProgramsSection';
import { WhereTheyShowUp } from './company/WhereTheyShowUp';
export function CompanyRecordPage() {
    const { id = '' } = useParams();
    const [params, setParams] = useSearchParams();
    const vehicleId = params.get('vehicle') ?? '';
    const queries = useCompany(id, vehicleId);
    const dossier = queries.dossier.data;
    const intel = useCompanyIntel(dossier?.companyId ?? '', dossier?.name ?? '');
    const documents = useRecordDocuments({
        kind: 'COMPANY',
        id
    }, !!dossier);
    const canEditRegistry = useTenantAreas().registries;
    const status = queries.dossier.isPending ? 'loading' : queries.dossier.isError ? 'error' : dossier ? 'ready' : 'unavailable';
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Companies",
        tab: "companies",
        title: dossier?.name,
        status: status,
        error: queries.dossier.error,
        onRetry: ()=>void queries.dossier.refetch(),
        header: dossier && /*#__PURE__*/ _jsx(CompanyHeader, {
            dossier: dossier,
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(CompanyUpload, {
                        id: id,
                        name: dossier.name,
                        onSettled: ()=>{
                            void queries.dossier.refetch();
                            void queries.rates.refetch();
                        }
                    }),
                    intel.action,
                    /*#__PURE__*/ _jsx(PartnerControls, {
                        companyId: dossier.companyId || id,
                        companyName: dossier.name,
                        designations: dossier.designations,
                        canEdit: canEditRegistry
                    })
                ]
            })
        }),
        railSections: dossier ? companyRail(dossier, intel.section, documents.data, documentsQueryState(documents)) : undefined,
        children: dossier && /*#__PURE__*/ _jsxs("div", {
            className: "space-y-8",
            "data-testid": Testids.registryCompany.page,
            children: [
                /*#__PURE__*/ _jsx(WhereTheyShowUp, {
                    cards: dossier.appearances
                }),
                /*#__PURE__*/ _jsx(CorporateHistory, {
                    companyId: dossier.companyId || id,
                    companyName: dossier.name,
                    lineage: dossier.lineage,
                    canEdit: canEditRegistry
                }),
                /*#__PURE__*/ _jsx(CompanyFamilySection, {
                    companyId: dossier.companyId || id
                }),
                /*#__PURE__*/ _jsx(SbaProgramsSection, {
                    companyId: id,
                    companyName: dossier.name
                }),
                queries.rates.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: "Loading company rates"
                }) : queries.rates.isError ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: ()=>void queries.rates.refetch(),
                    children: "Retry loading company rates"
                }) : /*#__PURE__*/ _jsx(CompanyRateBookTable, {
                    book: queries.rates.data,
                    vehicleId: vehicleId,
                    onVehicleChange: (next)=>{
                        const p = new URLSearchParams(params);
                        if (next) p.set('vehicle', next);
                        else p.delete('vehicle');
                        setParams(p);
                    }
                }),
                queries.people.isPending ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                    label: "Loading registry people"
                }) : queries.people.isError ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: ()=>void queries.people.refetch(),
                    children: "Retry loading registry people"
                }) : /*#__PURE__*/ _jsx(PeopleWeKnow, {
                    people: queries.people.data.people,
                    companyName: dossier.name
                }),
                intel.dialog
            ]
        })
    });
}
